import { createElement, useEffect, useRef } from 'react';
import { Flex, StackScreen } from '@banhala/ui-kit';
import { useNavigation } from '@react-navigation/native';
import { useHandleBackPressed } from 'shared/src/hooks';

type ZipcodeNavigation = {
  popTo: (
    name: 'Editor',
    params: { zipcodeResult: { zipcode: string; address: string } },
    options?: { merge?: boolean },
  ) => void;
  goBack: () => void;
  canGoBack: () => boolean;
};

type PostcodeData = {
  userSelectedType?: string;
  zonecode?: string;
  roadAddress?: string;
  jibunAddress?: string;
};

type PostcodeConstructor = new (options: {
  oncomplete: (data: PostcodeData) => void;
  width?: string;
  height?: string;
}) => { embed: (element: HTMLElement) => void };

type PostcodeWindow = Window & {
  kakao?: { Postcode?: PostcodeConstructor };
  daum?: { Postcode?: PostcodeConstructor };
};

// native ZipcodeSearch의 inline HTML과 동일한 카카오 postcode 번들
const POSTCODE_SCRIPT_URL =
  'https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

const getPostcodeConstructor = (): PostcodeConstructor | undefined => {
  const win = window as PostcodeWindow;
  return win.kakao?.Postcode ?? win.daum?.Postcode;
};

const loadPostcodeScript = (): Promise<PostcodeConstructor> =>
  new Promise((resolve, reject) => {
    const loaded = getPostcodeConstructor();
    if (loaded) {
      resolve(loaded);
      return;
    }
    const script = document.createElement('script');
    script.src = POSTCODE_SCRIPT_URL;
    script.onload = () => {
      const constructor = getPostcodeConstructor();
      if (constructor) {
        resolve(constructor);
      } else {
        reject(new Error('postcode constructor not found'));
      }
    };
    script.onerror = () => reject(new Error('postcode script load failed'));
    document.head.appendChild(script);
  });

/**
 * 우편번호 검색 — 웹 변형.
 * native는 RNAblyWebView(네이티브 WebView)에 inline HTML로 위젯을 띄우지만,
 * 웹은 같은 페이지 컨텍스트라 카카오 postcode 위젯을 div에 직접 embed하고
 * oncomplete 콜백을 바로 받는다 (postToNative 메시지 채널 불필요).
 */
const ZipcodeSearch = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const navigation = useNavigation<ZipcodeNavigation>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadPostcodeScript()
      .then(Postcode => {
        if (cancelled || containerRef.current === null) return;
        new Postcode({
          oncomplete: data => {
            // native 동일: userSelectedType R=도로명, 그 외 지번
            const isRoadAddress = (data.userSelectedType ?? 'R') === 'R';
            navigation.popTo(
              'Editor',
              {
                zipcodeResult: {
                  zipcode: data.zonecode ?? '',
                  address:
                    (isRoadAddress ? data.roadAddress : data.jibunAddress) ??
                    '',
                },
              },
              { merge: true },
            );
          },
          width: '100%',
          height: '100%',
        }).embed(containerRef.current);
      })
      .catch(() => {
        if (navigation.canGoBack()) navigation.goBack();
      });

    return () => {
      cancelled = true;
    };
  }, [navigation]);

  return (
    <StackScreen
      title="우편번호 검색"
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
    >
      <Flex flex={1}>
        {createElement('div', {
          ref: containerRef,
          style: { width: '100%', height: '100%' },
        })}
      </Flex>
    </StackScreen>
  );
};

export default ZipcodeSearch;
